import conversions from '@sstdev/lib_epc-conversions';
import logging from '@sstdev/lib_logging';
import { rfidStrategy } from 'lib_ui-services';

const _p = {
    getBrowserTimezone
};

// Server-side exports format date columns after the browser hands off column
// metadata. Some environments report "Etc/Unknown", which Node rejects during
// CSV generation, so the UI sends UTC when it cannot provide a real IANA zone.
const DEFAULT_EXPORT_TIMEZONE = 'UTC';

export const _private = _p;

/**
 * Transforms the value of records based on the feature flags enabled and the navigation context
 *
 * Filtering by the selected inventory used to be gated behind the `multiInventory` feature
 * flag (added in #6685 for tenants running multiple *concurrently active* inventories).
 * That scoping assumed a non-multiInventory item's `inventory` array holds at most one entry
 * (closing an inventory `$pull`s its entry from every item), so there was rarely anything to
 * filter. In practice the array can still carry stale/historical entries outside that
 * scenario, producing the same "every inventory joined together" symptom as #1149's export
 * bug on tenants without the flag. This was always a correctness fix, not a gated feature, so
 * it now always runs - `filterInventoryByNavigationSelection` is a no-op whenever there's no
 * `navigationSelection` to filter by, so unrelated exports are unaffected.
 *
 * `records` may be the LokiJS view's own cached documents (`doingExport_namespace_relation.js`
 * sets `records = view.data`, and `applyDidGet` passes the same array through unchanged when
 * no `didGet` rule substitutes a new one) - the same objects the grid renders from. Neither
 * this function nor `filterInventoryByNavigationSelection` may mutate a record in place, or
 * exporting would permanently prune "historical inventory" entries off the client's cache;
 * the user would then see a blank entry for that inventory elsewhere in the app until the next
 * full resync. Always return a new array; never assign onto an existing record's property.
 *
 * @param {Object[]} records
 * @param {Object[]} columns
 * @param {string[]} [featureFlags]
 * @param {{namespace:string, relation:string, record: object, available:boolean, loading:boolean }} navigationSelection
 * @returns {Object[]} The transformed records - callers must use this return value; the input
 *  array is not mutated.
 */
export function transformRecords(records, columns, featureFlags = [], navigationSelection) {
    const displayInAscii = featureFlags.includes('displayInAscii');

    records = filterInventoryByNavigationSelection(records, navigationSelection);

    const propertiesToTransformToAscii = columns
        .filter(column => column.dataType === 'encodedText')
        .map(column => column.propertyName);

    records.forEach(record => {
        if (displayInAscii) transformToAscii(record, propertiesToTransformToAscii);
    });

    return records;
}

/**
 * Filters out the inventories that are not part of the selected inventory based on the
 * navigation context. Non-mutating - see the note on `transformRecords` above for why: this
 * used to filter `item.inventory` in place, which is safe only while it's gated behind a
 * feature flag most tenants don't have. Now that it always runs, mutating a record that may be
 * the grid's own cached document would silently prune the client's local data.
 *
 * @param {Object[]} records
 * @param {{namespace:string, relation:string, record: object, available:boolean, loading:boolean }} navigationSelection
 * @returns {Object[]} A new array; items without a matching inventory array are returned as-is
 *  (same reference), items that were filtered are shallow copies with a new `inventory` array.
 */
export function filterInventoryByNavigationSelection(records, navigationSelection) {
    const selectedInventoryId = navigationSelection?.record?._id;
    if (!selectedInventoryId) return records;
    return records.map(item => {
        if (!Array.isArray(item.inventory)) return item;
        return {
            ...item,
            inventory: item.inventory.filter(inventoryItem => {
                return (
                    inventoryItem['inventory:inventory'] &&
                    inventoryItem['inventory:inventory']._id === selectedInventoryId
                );
            })
        };
    });
}

/**
 * Takes one record and converts all encodedText properties to ascii
 * @param {Object} record
 * @param {string[]} propertiesToTransform
 */
export function transformToAscii(record, propertiesToTransform) {
    propertiesToTransform.forEach(property => {
        const value = record[property];
        // Exporting with no tagId will cause the export to fail.
        // Only decode when the stored hex is unambiguously printable ASCII;
        // otherwise keep the raw hex so exports don't contain gibberish like
        // "6S██s" for raw-hex tags on ASCII tenants (1050) — matching what the
        // grid/detail views now show.
        record[property] = value && rfidStrategy.canDisplayAsAscii(value) ? conversions.ascii.fromHex(value) : value || '';
    });
}

export async function applyDidGet(records, { namespace, relation }, dispatch) {
    return dispatch(
        {
            result: records
        },
        { verb: 'didGet', namespace, relation },
        true
    )
        .then(payload => {
            return payload?.result || records;
        })
        .catch(logging.error);
}

/**
 * Adds the browser's timezone to the dateTime and localVerboseDateTime columns
 * This is used to ensure that the exported data is set for the correct timezone when generating server-side exports
 * @param {Object[]} columns
 * @returns {Object[]}
 */
export function addTimezoneToColumns(columns) {
    const timezone = getSafeTimezone(_p.getBrowserTimezone());
    return columns.map(column => {
        if (column.dataType === 'localVerboseDateTime' || column.dataType === 'dateTime') {
            return { ...column, timezone };
        }
        return column;
    });
}

/**
 * Get the browser's timezone using Intl.DateTimeFormat.
 * @returns {string} The IANA timezone string.
 */
export function getBrowserTimezone() {
    return getSafeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
}

function getSafeTimezone(timezone) {
    if (isValidTimezone(timezone)) {
        return timezone;
    }

    return DEFAULT_EXPORT_TIMEZONE;
}

function isValidTimezone(timezone) {
    if (typeof timezone !== 'string' || timezone.length === 0) {
        return false;
    }

    // Use Intl as the source of truth because the reporting service uses the
    // same timezone option when it converts exported date values.
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(new Date(0));
        return true;
    } catch {
        return false;
    }
}
